// src/admin/AdminJourneysPage.js

import React, { useState, useEffect } from 'react';
import {
  collection,
  getDocs,
  doc,
  getDoc,
  addDoc,
  updateDoc,
  deleteDoc,
  arrayRemove,
} from 'firebase/firestore';
import { db } from '../firebase';

// Subcomponents
import AdminJourneyJourneys from './AdminJourneyJourneys';
import AdminTravelCalendar from './AdminTravelCalendar';

function AdminJourneysPage() {
  // ======================
  // Journeys State
  // ======================
  const [journeys, setJourneys] = useState([]);
  const [selectedJourney, setSelectedJourney] = useState({
    id: '',
    code: '',
    days: 0,
    cost: 0,
    paymentStatus: false,
    routeIds: [],
    startDate: '',
    userId: '',
  });

  // ======================
  // Routes, Users State
  // ======================
  const [routes, setRoutes] = useState([]);
  const [users, setUsers] = useState([]);

  // Error/Loading
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  // ======================
  // Fetch All Data on Mount
  // ======================
  useEffect(() => {
    let isMounted = true;

    const fetchData = async () => {
      setLoading(true);
      try {
        // Journeys, Routes, Users in parallel
        const [journeysSnap, routesSnap, usersSnap] = await Promise.all([
          getDocs(collection(db, 'journeys')),
          getDocs(collection(db, 'routes')),
          getDocs(collection(db, 'users')),
        ]);

        if (!isMounted) return;

        // Journeys
        const journeysData = journeysSnap.docs.map((docSnap) => ({
          ...docSnap.data(),
          id: docSnap.id,
        }));
        setJourneys(journeysData);

        // Routes
        const routesData = routesSnap.docs.map((docSnap) => ({
          ...docSnap.data(),
          id: docSnap.id,
        }));
        setRoutes(routesData);

        // Users
        const usersData = usersSnap.docs.map((docSnap) => ({
          ...docSnap.data(),
          id: docSnap.id,
        }));
        setUsers(usersData);

      } catch (err) {
        console.error('Error fetching data:', err);
        setError('Failed to load journeys, routes, or users');
      } finally {
        if (isMounted) setLoading(false);
      }
    };
    fetchData();

    return () => {
      isMounted = false;
    };
  }, []);

  // ======================
  // Journey Handlers
  // ======================
  const handleJourneyInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setSelectedJourney((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSaveJourneyChanges = async () => {
    if (!selectedJourney.code) {
      setError('Journey code is required');
      return;
    }

    try {
      // We'll dynamically build the journey "name" field
      let finalName = 'Unnamed Journey';

      // If userId is set, fetch that user
      if (selectedJourney.userId) {
        const userRef = doc(db, 'users', selectedJourney.userId);
        const userSnap = await getDoc(userRef);
        if (userSnap.exists()) {
          const userData = userSnap.data();
          const userFirstName = userData.firstName || 'User';

          // If routeIds exist, fetch the first route's name
          if (selectedJourney.routeIds.length > 0) {
            const firstRouteId = selectedJourney.routeIds[0];
            const routeRef = doc(db, 'routes', firstRouteId);
            const routeSnap = await getDoc(routeRef);
            if (routeSnap.exists()) {
              const routeData = routeSnap.data();
              const firstRouteName = routeData.name || 'Unknown Route';
              finalName = `${userFirstName}'s ${firstRouteName}`;
            } else {
              finalName = `${userFirstName}'s Route`;
            }
          } else {
            // If no routes, just user name
            finalName = `${userFirstName}'s Journey`;
          }
        }
      }

      // Build final journey doc
      const journeyDoc = {
        code: selectedJourney.code,
        name: finalName,
        days: selectedJourney.days,
        cost: selectedJourney.cost,
        paymentStatus: selectedJourney.paymentStatus,
        routeIds: selectedJourney.routeIds,
        startDate: selectedJourney.startDate,
        userId: selectedJourney.userId || '',
      };

      // If we have an "id", update; otherwise add
      if (selectedJourney.id) {
        await updateDoc(doc(db, 'journeys', selectedJourney.id), journeyDoc);
        alert('Journey updated successfully');
      } else {
        await addDoc(collection(db, 'journeys'), journeyDoc);
        alert('Journey added successfully');
      }

      // Reset selected journey
      setSelectedJourney({
        id: '',
        code: '',
        days: 0,
        cost: 0,
        paymentStatus: false,
        routeIds: [],
        startDate: '',
        userId: '',
      });

      // Re-fetch journeys
      const snap = await getDocs(collection(db, 'journeys'));
      const freshData = snap.docs.map((d) => ({
        ...d.data(),
        id: d.id,
      }));
      setJourneys(freshData);

    } catch (err) {
      console.error('Error saving journey:', err);
      setError('Failed to save journey');
    }
  };

  const handleEditJourney = (journey) => {
    setSelectedJourney({
      id: journey.id,
      code: journey.code ?? '',
      days: journey.days ?? 0,
      cost: journey.cost ?? 0,
      paymentStatus: journey.paymentStatus ?? false,
      routeIds: journey.routeIds ?? [],
      startDate: journey.startDate ?? '',
      userId: journey.userId ?? '',
    });
  };

  const handleDeleteJourney = async (journeyId) => {
    const confirmDelete = window.confirm(
      `Are you sure you want to delete this journey? This will also remove it from all user accounts. This action cannot be undone.`
    );

    if (!confirmDelete) return;

    try {
      console.log(`Starting deletion of journey: ${journeyId}`);
      
      // Step 1: Get all users who have this journey in their collections
      const usersSnapshot = await getDocs(collection(db, 'users'));
      const usersWithJourney = [];
      
      usersSnapshot.docs.forEach(userDoc => {
        const userData = userDoc.data();
        if (userData.journeys && userData.journeys.includes(journeyId)) {
          usersWithJourney.push({
            id: userDoc.id,
            email: userData.email,
            journeys: userData.journeys
          });
        }
      });

      console.log(`Found ${usersWithJourney.length} users with this journey`);

      // Step 2: Remove journey from all user collections
      for (const user of usersWithJourney) {
        console.log(`Removing journey ${journeyId} from user ${user.email}`);
        await updateDoc(doc(db, 'users', user.id), {
          journeys: arrayRemove(journeyId)
        });
      }

      // Step 3: Delete the journey document
      await deleteDoc(doc(db, 'journeys', journeyId));
      
      console.log(`Journey ${journeyId} deleted successfully`);
      alert(`Journey deleted successfully and removed from ${usersWithJourney.length} user account(s).`);

      // Step 4: Refresh journeys list
      const snap = await getDocs(collection(db, 'journeys'));
      const freshData = snap.docs.map((d) => ({
        ...d.data(),
        id: d.id,
      }));
      setJourneys(freshData);

    } catch (err) {
      console.error('Error deleting journey:', err);
      setError(`Failed to delete journey: ${err.message}`);
      alert(`Error deleting journey: ${err.message}`);
    }
  };

  // ======================
  // Associate Helpers
  // ======================

  // Show route names for each Journey
  const getAssociatedRoutes = (journey) => {
    if (!Array.isArray(journey.routeIds)) return 'No Routes';
    return journey.routeIds
      .map((rid) => {
        const r = routes.find((rt) => rt.id === rid);
        return r ? r.name : 'Unknown Route';
      })
      .join(', ');
  };

  if (loading) {
    return <div>Loading Journeys, Routes, & Users...</div>;
  }

  return (
    <div>
      <h2>Manage Journeys</h2>
      {error && <p style={{ color: 'red' }}>{error}</p>}

      {/* Journeys subcomponent */}
      <AdminJourneyJourneys
        journeys={journeys}
        selectedJourney={selectedJourney}
        handleJourneyInputChange={handleJourneyInputChange}
        handleSaveJourneyChanges={handleSaveJourneyChanges}
        handleEditJourney={handleEditJourney}
        handleDeleteJourney={handleDeleteJourney}
        routes={routes}
        getAssociatedRoutes={getAssociatedRoutes}
        users={users}
      />

      {/* Travel Calendar Section */}
      <AdminTravelCalendar />
    </div>
  );
}

export default AdminJourneysPage;